import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addMonths, endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import styled from 'styled-components';
import { apiFetch } from '../lib/api';

type InterviewSlot = {
  id: string;
  userId: string;
  utcDateTime: string;
  localDate: string;
  localTime: string;
  timezone: string;
  type: string;
  host?: {
    uid: string;
    name?: string | null;
    email?: string | null;
    role?: string | null;
  };
};

type InterviewAvailabilityResponse = {
  candidate: {
    id: string;
    name: string;
    email: string;
  };
  job: {
    id: string;
    title: string;
    requisitionId: string;
  };
  stage: {
    id: string;
    name: string;
  } | null;
  inviteExpiresAt: string;
  slots: InterviewSlot[];
};

const Page = styled.main`
  min-height: 100vh;
  padding: 40px 20px 64px;
  background: radial-gradient(circle at 12% 8%, rgba(224, 161, 94, 0.24), transparent 38%),
    radial-gradient(circle at 92% 0%, rgba(210, 82, 64, 0.2), transparent 36%),
    linear-gradient(180deg, #050a1e 0%, #040816 100%);
  color: #f5f7ff;
`;

const Wrap = styled.div`
  width: min(980px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 16px;
`;

const Card = styled.section`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  background: rgba(14, 20, 38, 0.82);
  padding: 18px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 2.6vw, 2.4rem);
  color: #e0a15e;
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.82);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.92);
  min-width: 38px;
  min-height: 34px;
  cursor: pointer;
`;

const Month = styled.div`
  font-weight: 700;
`;

const SlotsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const SlotGroup = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const SlotDate = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
`;

const SlotList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SlotButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? '#e0a15e' : 'rgba(255, 255, 255, 0.3)')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#e0a15e' : 'transparent')};
  color: ${({ $active }) => ($active ? '#141414' : 'rgba(255, 255, 255, 0.9)')};
  font-weight: 700;
  padding: 8px 12px;
  cursor: pointer;
`;

const Input = styled.input`
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  min-height: 40px;
  background: transparent;
  color: #fff;
  padding: 0 12px;
`;

const Textarea = styled.textarea`
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  min-height: 90px;
  background: transparent;
  color: #fff;
  padding: 10px 12px;
`;

const Action = styled.button`
  border: 0;
  border-radius: 999px;
  min-height: 42px;
  padding: 0 18px;
  font-weight: 800;
  background: #e0a15e;
  color: #151515;
  cursor: pointer;
`;

const Meta = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.74);
  font-size: 0.9rem;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #ff9c9c;
`;

const SuccessText = styled.p`
  margin: 0;
  color: #8fd6a3;
`;

export default function CareerInterviewSchedulerPage() {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [payload, setPayload] = useState<InterviewAvailabilityResponse | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const viewerTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );

  const start = format(startOfMonth(month), 'yyyy-MM-dd');
  const end = format(endOfMonth(month), 'yyyy-MM-dd');
  const safeInviteToken = inviteToken?.trim() ?? '';

  const formatUtcInViewerTimezone = (utcIso: string, pattern: string) =>
    format(toZonedTime(parseISO(utcIso), viewerTimezone), pattern);

  useEffect(() => {
    if (!safeInviteToken) return;
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const data = await apiFetch<InterviewAvailabilityResponse>(
          `/api/careers/interview/${encodeURIComponent(safeInviteToken)}/availability?start=${start}&end=${end}&timezone=${encodeURIComponent(viewerTimezone)}`,
        );
        if (!cancelled) {
          setPayload(data);
          setSelectedSlotId((current) =>
            current && data.slots.some((slot) => slot.id === current) ? current : '',
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setPayload(null);
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Unable to load interview availability.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [end, safeInviteToken, start, viewerTimezone]);

  const groupedSlots = useMemo(() => {
    const rows = payload?.slots ?? [];
    const map = new Map<string, InterviewSlot[]>();
    for (const slot of rows) {
      const viewerDate = formatUtcInViewerTimezone(slot.utcDateTime, 'yyyy-MM-dd');
      const list = map.get(viewerDate) ?? [];
      list.push(slot);
      map.set(viewerDate, list);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, slots]) => ({
        date,
        slots: slots.sort((a, b) => a.utcDateTime.localeCompare(b.utcDateTime)),
      }));
  }, [payload?.slots, viewerTimezone]);

  const selectedSlot = useMemo(
    () => payload?.slots.find((slot) => slot.id === selectedSlotId) ?? null,
    [payload?.slots, selectedSlotId],
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!safeInviteToken || !selectedSlotId) {
      setError('Please select an available interview time.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await apiFetch<{
        startsAt: string;
        timezone: string;
        phaseName: string;
      }>(`/api/careers/interview/${encodeURIComponent(safeInviteToken)}/schedule`, {
        method: 'POST',
        body: JSON.stringify({
          slotId: selectedSlotId,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
        }),
      });
      setSuccess(
        `Interview scheduled for ${formatUtcInViewerTimezone(result.startsAt, 'MMMM d, yyyy h:mm a')} (${viewerTimezone}).`,
      );
      setSelectedSlotId('');
      setTitle('');
      setDescription('');
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to schedule interview.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <Wrap>
        <Card>
          <Title>Schedule your interview</Title>
          <Sub>
            {payload
              ? `${payload.job.title} (${payload.job.requisitionId})${
                  payload.stage ? ` - ${payload.stage.name}` : ''
                }`
              : 'Select a time that works for you from available interviewer slots.'}
          </Sub>
          {payload?.inviteExpiresAt ? (
            <Meta>
              This invite expires on {new Date(payload.inviteExpiresAt).toLocaleString()}.
            </Meta>
          ) : null}
        </Card>

        <Card>
          <Row>
            <NavButton type="button" onClick={() => setMonth((prev) => subMonths(prev, 1))}>
              {'<'}
            </NavButton>
            <Month>{format(month, 'MMMM yyyy')}</Month>
            <NavButton type="button" onClick={() => setMonth((prev) => addMonths(prev, 1))}>
              {'>'}
            </NavButton>
          </Row>

          {loading ? <Meta>Loading availability...</Meta> : null}
          {!loading && groupedSlots.length === 0 ? (
            <Meta>No interview slots are available for this date range.</Meta>
          ) : null}

          <SlotsGrid>
            {groupedSlots.map((group) => (
              <SlotGroup key={group.date}>
                <SlotDate>{format(parseISO(`${group.date}T00:00:00`), 'EEEE, MMM d')}</SlotDate>
                <SlotList>
                  {group.slots.map((slot) => (
                    <SlotButton
                      key={slot.id}
                      type="button"
                      $active={slot.id === selectedSlotId}
                      onClick={() => setSelectedSlotId(slot.id)}
                    >
                      {formatUtcInViewerTimezone(slot.utcDateTime, 'h:mm a')}
                      {slot.host?.name ? ` - ${slot.host.name}` : ''}
                    </SlotButton>
                  ))}
                </SlotList>
              </SlotGroup>
            ))}
          </SlotsGrid>
        </Card>

        <Card as="form" onSubmit={submit}>
          <Meta>
            Selected slot:{' '}
            {selectedSlot
              ? `${formatUtcInViewerTimezone(selectedSlot.utcDateTime, 'MMMM d, yyyy h:mm a')} (${viewerTimezone})`
              : 'None'}
          </Meta>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Optional interview title"
          />
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional notes for the interview"
          />
          <Row>
            <Action type="submit" disabled={saving || !selectedSlotId}>
              {saving ? 'Scheduling...' : 'Confirm interview'}
            </Action>
            <Link to="/careers" style={{ color: '#e0a15e', fontWeight: 700, alignSelf: 'center' }}>
              Back to careers
            </Link>
          </Row>
          {error ? <ErrorText>{error}</ErrorText> : null}
          {success ? <SuccessText>{success}</SuccessText> : null}
        </Card>
      </Wrap>
    </Page>
  );
}
